import React, { useState } from 'react';
import { Camera, FileImage, Upload, Plus, Eye, Download } from 'lucide-react';

interface MediaDocumentsTabProps {
  enquete: any;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onAddPhoto?: () => void;
  onAddFile?: () => void;
}

const MediaDocumentsTab: React.FC<MediaDocumentsTabProps> = ({
  enquete,
  activeTab = 'photos',
  onTabChange,
  onAddPhoto,
  onAddFile
}) => {
  const [currentTab, setCurrentTab] = useState(activeTab);

  const handleTabChange = (tab: string) => {
    setCurrentTab(tab);
    onTabChange?.(tab);
  };

  const subTabs = [
    { 
      id: 'photos', 
      label: 'Photos', 
      icon: Camera, 
      count: enquete?.photos?.length || 0,
      onAdd: onAddPhoto 
    },
    { 
      id: 'fichiers', 
      label: 'Fichiers', 
      icon: FileImage, 
      count: enquete?.fichiers?.length || 0,
      onAdd: onAddFile 
    }
  ];

  const renderPhotosSection = () => (
    <div className="space-y-4">
      {enquete?.photos?.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {enquete.photos.map((photo: any, index: number) => (
            <div key={index} className="enhanced-card p-4 group">
              <div className="aspect-square bg-osint-light-gray/20 rounded-lg mb-3 flex items-center justify-center overflow-hidden relative">
                {photo.url ? (
                  <img 
                    src={photo.url} 
                    alt={photo.nom || `Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Camera className="w-12 h-12 text-gray-500" />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                  <button className="p-2 bg-osint-orange rounded-lg hover:bg-orange-600 transition-colors">
                    <Eye className="w-4 h-4 text-white" />
                  </button>
                  <button className="p-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                    <Download className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
              <h4 className="text-white font-medium text-sm mb-1 truncate">
                {photo.nom || `Photo ${index + 1}`}
              </h4>
              {photo.date && (
                <p className="text-gray-400 text-xs">{photo.date}</p>
              )}
              {photo.description && (
                <p className="text-gray-300 text-xs mt-2 line-clamp-2">{photo.description}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Camera className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune photo ajoutée</h3>
          <p className="text-gray-400 mb-6">Ajoutez des photos pertinentes à cette enquête</p>
          <button 
            onClick={onAddPhoto}
            className="empty-state-action"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une photo
          </button>
        </div>
      )}
    </div>
  );

  const renderFichiersSection = () => (
    <div className="space-y-4">
      {enquete?.fichiers?.length > 0 ? (
        <div className="space-y-3">
          {enquete.fichiers.map((fichier: any, index: number) => (
            <div key={index} className="enhanced-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="w-12 h-12 bg-osint-orange/20 rounded-lg flex items-center justify-center">
                    <FileImage className="w-6 h-6 text-osint-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate">
                      {fichier.nom || `Fichier ${index + 1}`}
                    </h4>
                    <div className="flex items-center space-x-4 text-sm text-gray-400 mt-1">
                      {fichier.taille && (
                        <span>{fichier.taille}</span>
                      )}
                      {fichier.type && (
                        <span>{fichier.type.toUpperCase()}</span>
                      )}
                      {fichier.date && (
                        <span>{fichier.date}</span>
                      )}
                    </div>
                    {fichier.description && (
                      <p className="text-gray-300 text-sm mt-2 line-clamp-2">{fichier.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 bg-osint-orange rounded-lg hover:bg-orange-600 transition-colors">
                    <Eye className="w-4 h-4 text-white" />
                  </button>
                  <button className="p-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                    <Download className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Upload className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun fichier ajouté</h3>
          <p className="text-gray-400 mb-6">Ajoutez des documents et fichiers pertinents</p>
          <button 
            onClick={onAddFile}
            className="empty-state-action"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un fichier
          </button>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (currentTab) {
      case 'photos':
        return renderPhotosSection();
      case 'fichiers':
        return renderFichiersSection();
      default:
        return renderPhotosSection();
    }
  };

  return (
    <div className="media-documents-tab">
      {/* Navigation à onglets */}
      <div className="tab-navigation">
        {subTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              <span>{tab.label}</span>
              <span className="ml-2 px-2 py-1 text-xs bg-osint-orange/20 text-osint-orange rounded-full">
                {tab.count}
              </span>
              {currentTab === tab.id && tab.onAdd && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    tab.onAdd?.();
                  }}
                  className="ml-auto p-1 hover:bg-osint-orange/20 rounded"
                  title={`Ajouter ${tab.label.toLowerCase()}`}
                >
                  <Plus className="w-3 h-3" />
                </button>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenu de l'onglet */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default MediaDocumentsTab;